import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck } from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";
import "./QuickTaskFab.css";

/**
 * Botao flutuante de lancamento rapido de tarefa - o que / quem / quando.
 * Montado uma vez no AppLayout (igual o PwaInstallPrompt), entao acompanha
 * todas as paginas autenticadas sem precisar adicionar em cada uma.
 */
const QuickTaskFab = () => {
  const { user, isAuthenticated } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();

  const [show, setShow] = useState(false);
  const [membros, setMembros] = useState([]);
  const [descricao, setDescricao] = useState("");
  const [idUsuarioResponsavel, setIdUsuarioResponsavel] = useState("");
  const [vencimento, setVencimento] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!show || !selectedWorkspaceId) return;
    api
      .get(`/workspaces/${selectedWorkspaceId}/members`)
      .then((res) => setMembros(res.data?.records || res.data || []))
      .catch(() => setMembros([]));
  }, [show, selectedWorkspaceId]);

  useEffect(() => {
    if (!show) return;
    setDescricao("");
    setIdUsuarioResponsavel(user?.id ? String(user.id) : "");
    setVencimento("");
    setErro("");
    setSucesso(false);
    // Reabrir sempre comeca com "quem" = o proprio usuario, trocavel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!isAuthenticated || !selectedWorkspaceId) return null;

  async function salvar() {
    if (!descricao.trim()) return;
    setSalvando(true);
    setErro("");
    try {
      await api.post("/tasks", {
        fields: {
          descricao: descricao.trim(),
          id_workspace: selectedWorkspaceId,
          id_usuario_responsavel: idUsuarioResponsavel || null,
          vencimento: vencimento || null,
          concluido: false,
        },
      });
      setSucesso(true);
      setTimeout(() => setShow(false), 1100);
    } catch (e) {
      setErro(e?.response?.data?.message || "Nao foi possivel criar a tarefa.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="quick-task-fab"
        onClick={() => setShow(true)}
        title="Nova tarefa rapida"
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>

      {show && (
        <Modal
          show
          onHide={() => setShow(false)}
          centered
          dialogClassName="quick-task-dialog"
          contentClassName="quick-task-content"
        >
          <Modal.Header closeButton>
            <Modal.Title>Nova tarefa</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {erro && <Alert variant="danger">{erro}</Alert>}
            {sucesso ? (
              <div className="quick-task-sucesso">
                <FontAwesomeIcon icon={faCheck} size="2x" />
                <p>Tarefa criada!</p>
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>O que precisa ser feito?</Form.Label>
                  <Form.Control
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Ex.: Ligar para o cliente X"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && descricao.trim()) salvar();
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Quem</Form.Label>
                  <Form.Select
                    value={idUsuarioResponsavel}
                    onChange={(e) => setIdUsuarioResponsavel(e.target.value)}
                  >
                    <option value="">Sem responsavel definido</option>
                    {membros.map((m) => (
                      <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>
                        {m.descricao || m.nome || m.email}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Quando (opcional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={vencimento}
                    onChange={(e) => setVencimento(e.target.value)}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          {!sucesso && (
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShow(false)}>Cancelar</Button>
              <Button variant="primary" onClick={salvar} disabled={salvando || !descricao.trim()}>
                {salvando ? "Salvando..." : "Criar tarefa"}
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </>
  );
};

export default QuickTaskFab;
